import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../ApplicationsDashboard/ApplicationsDashboard.css";
import "./DeletedApplicationsDashboard.css";
import SeedsLogo from "../../assets/images/SeedsLogo.png";
import { useDispatch, useSelector } from "react-redux";
import { getApplicationStatusList, getDeletedApplications } from "../../redux/ApplicationReducer/action";
import UserProfileDropdown from "../../components/UserProfile/UserProfile";
import CustomPagination from "../../components/Pagination/CustomPagination";
import Sidebar from "../../components/Sidebar/Sidebar";
import { getEligibleOfficers, getOfficers } from "../../redux/UserReducer/action";
import { DELETED_APPLICATION_COLUMN_RULES } from "../../config/application";
import { toast } from "react-toastify";
import axios from "axios";
import { downloadFile, isAuthorised } from "../../utils/common";
import FilterModal from "../Reports/FilterModal/FilterModal";
import { resetFilters } from "../../utils/reportUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faFilter, faDownload } from "@fortawesome/free-solid-svg-icons";
import FunnelIconSvg from "../../components/SVG/FunnelIconSvg";
import { getAuthToken } from "../../utils/auth";

const DeletedApplicationsDashboard = () => {
    const navigate = useNavigate();

    const [allowedStages, setAllowedStages] = useState([]);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const searchInputRef = useRef(null);
    

    const [isDownloadingDeletedApps, setIsDownloadingDeletedApps] = useState(false);

    // Get auth token
    const authToken = useMemo(() => {
        // const tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
        // return tokens?.token || null;
        return getAuthToken();
    }, []);

    let headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-type': 'application/json'
    }

    const officer = JSON.parse(sessionStorage.getItem("officer"));

    const { applications, applicationStatusList, officers } = useSelector((store) => {
        return {
            applications: store.ApplicationReducer.deletedApplications.data,
            pagination: store.ApplicationReducer.deletedApplications.pagination,
            applicationStatusList: store.ApplicationReducer.applicationStatusList,
            officers: store.UserReducer.officers.officers,
        }
    }, []);

    const dispatch = useDispatch();
    const location = useLocation();

    const storedFilters = JSON.parse(sessionStorage.getItem('deletedApplicationFilters')) || {};

    const [deletedApplicationFilters, setDeletedApplicationFilters] = useState({
        ro_employee_ids: storedFilters?.ro_employee_ids || [],
        bm_employee_ids: storedFilters?.bm_employee_ids || [],
        bcm_employee_ids: storedFilters?.bcm_employee_ids || [],
        application_id: storedFilters?.application_id || "",
        application_status: storedFilters?.application_status || "",
        current_application_bucket: storedFilters?.current_application_bucket || "",
        application_stage_name: storedFilters?.application_stage_name || "",
        customer_name: storedFilters?.customer_name || "",
        customer_pan: storedFilters?.customer_pan || "",
        phone_number: storedFilters?.phone_number || "",
        branch_name: storedFilters?.branch_name || "",
        branch_code: storedFilters?.branch_code || "",
        lead_id: storedFilters?.lead_id || "",
        seeds_id: storedFilters?.seeds_id || "",
        sort_order: storedFilters?.sort_order || "",
        sort_by: storedFilters?.sort_by || "",
        global_search: storedFilters?.global_search || ""
    });

    const [showFilterModal, setShowFilterModal] = useState(false);

    const [dateRange, setDateRange] = useState([
        deletedApplicationFilters?.start_date ? new Date(deletedApplicationFilters.start_date) : null,
        deletedApplicationFilters?.end_date ? new Date(deletedApplicationFilters.end_date) : null,
    ]);

    const [startDate, endDate] = dateRange;

    const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('currentPage')) || 1);
    const [limit, setLimit] = useState(parseInt(sessionStorage.getItem('limit')) || 10);
    const totalCount = parseInt(sessionStorage.getItem('totalCount')) || 0;
    const totalPages = Math.ceil(totalCount / limit);

    useEffect(() => {
        dispatch(getDeletedApplications(currentPage, limit, deletedApplicationFilters));

        dispatch(getEligibleOfficers(1, 2000, {}));

        sessionStorage.setItem('currentPage', currentPage);
        sessionStorage.setItem('limit', limit);
        sessionStorage.setItem('deletedApplicationFilters', JSON.stringify(deletedApplicationFilters));
    }, [dispatch, limit, currentPage, deletedApplicationFilters.global_search]);

    useEffect(() => {
        dispatch(getApplicationStatusList({
            is_active: true
        }));
    }, [dispatch]);

    useEffect(() => {
        let officerFilters = {
            is_active: true
        };

        dispatch(getOfficers(1, 1000, officerFilters));
    }, [dispatch]);

    const handlePageChange = (newPage) => {
        // Ensure we're working with 1-based pagination
        const pageToSet = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(pageToSet);
        sessionStorage.setItem('currentPage', pageToSet.toString());
    };

    const handleDateRangeChange = (update) => {
        setDateRange(update);

        const [start, end] = update;

        const formatDate = (date) => {
            if (!date) return "";
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const updatedFilters = {
            ...deletedApplicationFilters,
            start_date: formatDate(start),
            end_date: formatDate(end),
        };

        setDeletedApplicationFilters(updatedFilters);
        sessionStorage.setItem("deletedApplicationFilters", JSON.stringify(updatedFilters));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        let updatedFilters = {
            ...deletedApplicationFilters,
            [name]: value
        };

        // If parent stage changes → reset application stage & sync parent_stage_name/current_application_bucket
        if (name === "parent_stage_name" || name === "current_application_bucket") {
            updatedFilters.parent_stage_name = value;
            updatedFilters.current_application_bucket = value;
            updatedFilters.application_stage_name = "";
        }

        setDeletedApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("deletedApplicationFilters", JSON.stringify(updatedFilters));

        if (name === "global_search" && value.trim() === "") {
            dispatch(getDeletedApplications(1, limit, updatedFilters));
        }
    };

    const getActiveFilterCount = () => {
        let count = 0;
        Object.keys(deletedApplicationFilters).forEach((key) => {
            if (key === 'global_search') return;
            const val = deletedApplicationFilters[key];
            if (Array.isArray(val) && val.length > 0) count++;
            else if (val !== '' && val !== null && val !== undefined) count++;
        });
        return count;
    };

    const handleClearFilters = () => {
        const cleared = {
            ro_employee_ids: [],
            bm_employee_ids: [],
            bcm_employee_ids: [],
            application_id: "",
            application_status: "",
            current_application_bucket: "",
            application_stage_name: "",
            customer_name: "",
            customer_pan: "",
            phone_number: "",
            branch_name: "",
            branch_code: "",
            lead_id: "",
            seeds_id: "",
            sort_order: "",
            sort_by: "",
            global_search: ""
        };
        setDeletedApplicationFilters(cleared);
        sessionStorage.setItem('deletedApplicationFilters', JSON.stringify(cleared));
        dispatch(getDeletedApplications(1, limit, cleared));
    };

    const masterStatusList = applicationStatusList || [];

    // Distinct Parent Stages
    const parentStageOptions = React.useMemo(() => {
        return [
            ...new Set(
                masterStatusList?.map(item => item.parent_stage_name)
            )
        ]
        .map(stage => ({
            label: stage,
            value: stage
        }));
    }, [masterStatusList]);

    // Application Stages based on selected parent
    const applicationStageOptions = React.useMemo(() => {
        // If no parent selected → show all application stages
        if (!deletedApplicationFilters.current_application_bucket) {
            return [
                ...new Set(
                    masterStatusList?.map(item => item.application_stage_name)
                )
            ]
                .map(stage => ({
                    label: stage,
                    value: stage
                }));
        }

        // If parent selected → show only related application stages
        return [
            ...new Set(
                masterStatusList
                    ?.filter(
                        item =>
                            item.parent_stage_name === deletedApplicationFilters.current_application_bucket
                    )
                    ?.map(item => item.application_stage_name)
            )
        ]
            .map(stage => ({
                label: stage,
                value: stage
            }));
    }, [masterStatusList, deletedApplicationFilters.current_application_bucket]);

    const officerOptions = React.useMemo(() => {
        const roOptions = [];
        const bmOptions = [];
        const bcmOptions = [];

        officers?.forEach(branch => {
            branch?.officer_details?.forEach(officer => {
                const option = {
                    label: `${officer.emp_id} - ${officer.first_name} ${officer.last_name || ""}`.trim(),
                    value: officer.emp_id
                };

                if (officer.role_code === "RO") {
                    roOptions.push(option);
                }

                if (officer.role_code === "BM") {
                    bmOptions.push(option);
                }

                if (officer.role_code === "BCM") {
                    bcmOptions.push(option);
                }
            });
        });

        // remove duplicate emp_ids
        const uniqueByValue = (arr) => {
            return arr.filter(
                (item, index, self) =>
                    index === self.findIndex(obj => obj.value === item.value)
            );
        };

        return {
            roOptions: uniqueByValue(roOptions),
            bmOptions: uniqueByValue(bmOptions),
            bcmOptions: uniqueByValue(bcmOptions),
        };
    }, [officers]);

    // console.log({ masterStatusList, applicationStatusList, officers })

    return (
        <div className="applicationsDashboardContainer">
            <div className="applicationsDashboard">
                <Sidebar />

                <div className="dashboardMainContent">
                    <div className="contentHeader">
                        <div className="userLogo">
                            <img src={SeedsLogo} alt="logo" className="SeedsLogo" />
                        </div>
                        <div className="headerRightPanel">
                            <UserProfileDropdown />
                        </div>
                    </div>

                    <div className="pageContentWrapper">
                        <div className="pageHeaderSection">
                            <div className="pageActions">
                                <h2>Deleted Applications</h2>
                            </div>
                        </div>

                        <div className="searchControlSection">
                            <div className="tableSearchBar">
                                <input
                                    type="search"
                                    placeholder="Search across PAN, phone, name, lead ID, seeds ID, branch"
                                    name="global_search"
                                    value={deletedApplicationFilters?.global_search}
                                    onChange={handleFilterChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            dispatch(getDeletedApplications(1, limit, deletedApplicationFilters));
                                        }
                                    }}
                                />
                            </div>
                            <div className="searchActionButtons">
                                <button
                                    className="searchButton"
                                    onClick={handleClearFilters}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>Clear Filters</span>
                                </button>

                                {/* <button 
                                    className="downloadButton"
                                    onClick={() => {
                                        let endpoint = `${process.env.REACT_APP_API_BASE_URL}/applications/get-deleted-applications`;

                                        downloadFile("deleted-applications", endpoint, deletedApplicationFilters);
                                    }}
                                >
                                    Download
                                </button> */}

                                <button
                                    className="downloadButton"
                                    disabled={isDownloadingDeletedApps}
                                    onClick={async () => {
                                        if (isDownloadingDeletedApps) return;
                                        setIsDownloadingDeletedApps(true);
                                        try {
                                            let endpoint = `${process.env.REACT_APP_API_BASE_URL}/applications/get-deleted-applications`;
                                            await downloadFile("deleted-applications", endpoint, deletedApplicationFilters);
                                        } finally {
                                            setIsDownloadingDeletedApps(false);
                                        }
                                    }}
                                >
                                    {isDownloadingDeletedApps ? (
                                        <>
                                            <span className="buttonSpinner"></span>
                                            Downloading...
                                        </>
                                    ) : (
                                        'Download'
                                    )}
                                </button>

                                <button
                                    className="filterButton"
                                    onClick={() => setShowFilterModal(true)}
                                >
                                    <FunnelIconSvg fillColor={"black"} />
                                </button>
                            </div>
                        </div>

                        <div className="tableCard">
                            <div className="applicationsTableContainer">
                                <table className="applications-table">
                                    <thead>
                                        <tr className="deletedApplicationHeaderRow">
                                            <th>Application ID</th>
                                            <th>Seeds ID</th>
                                            <th>RO Name</th>
                                            <th>RO Employee ID</th>
                                            <th>BM Name</th>
                                            <th>BM Employee ID</th>
                                            <th>BCM Name</th>
                                            <th>BCM Employee ID</th>
                                            <th>Branch Code</th>
                                            <th>Branch</th>
                                            <th>Loan Amount</th>
                                            <th>External Customer ID</th>
                                            <th>Existing Customer</th>
                                            <th>Customer Name</th>
                                            <th>Customer Email</th>
                                            <th>Customer Phone Number</th>
                                            <th>Customer Aadhaar Number</th>
                                            <th>Customer PAN Number</th>
                                            <th>Parent Stage</th>
                                            <th>Application Stage</th>
                                            <th>Application Creation Date</th>
                                            <th>Application Updated Date</th>
                                            {/* <th>Has Female Applicant</th>
                                    <th>RO Flow Completed</th>
                                    <th>BM Flow Completed</th>
                                    <th>BCM Flow Completed</th> */}
                                    <th>Lead ID</th>
                                    <th>Deleted By</th>
                                    <th>Deletion Officer Role</th>
                                    <th>Deletion Reason</th>
                                    <th>Deleted Date</th>
                                    <th>Is Rejected</th>
                                    <th>Rejected By</th>
                                    <th>Rejection Reason</th>
                                    <th>Rejected Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    applications?.length === 0 ? (
                                        <tr>
                                            <td colSpan={31} className="no-data-cell">
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        applications?.map((application, index) => {
                                            const roLabel = [application?.ro?.first_name?.trim(), application?.ro?.middle_name?.trim(), application?.ro?.last_name?.trim()].filter(Boolean).join(" ");
                                            const bmLabel = [application?.bm?.first_name?.trim(), application?.bm?.middle_name?.trim(), application?.bm?.last_name?.trim()].filter(Boolean).join(" ");
                                            const bcmLabel = [application?.bcm?.first_name?.trim(), application?.bcm?.middle_name?.trim(), application?.bcm?.last_name?.trim()].filter(Boolean).join(" ");

                                            const deleteActionOfficer = [application?.deletion_reason?.officer?.first_name?.trim(), application?.deletion_reason?.officer?.last_name?.trim()].filter(Boolean).join(" ");

                                            return (
                                                <tr className="deletedApplicationDataRow" key={application?.application_id}>
                                                    <td
                                                        className="blueText"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => {
                                                            const selectedText = window.getSelection().toString();
                                                            if (!selectedText) {
                                                                navigate(`/applications/${application.application_id}?from=${encodeURIComponent(location.pathname)}`);
                                                            }
                                                        }}
                                                    >
                                                        {application.application_id}
                                                    </td>
                                                    <td>
                                                        {application.seeds_id}
                                                    </td>
                                                    <td>{roLabel}</td>
                                                    <td>{application?.ro?.emp_id}</td>
                                                    <td>{bmLabel}</td>
                                                    <td>{application?.bm?.emp_id}</td>
                                                    <td>{bcmLabel}</td>
                                                    <td>{application?.bcm?.emp_id}</td>
                                                    <td>{application?.branch_code}</td>
                                                    <td>{application?.branch_name}</td>
                                                    <td>{application?.loan_amount}</td>
                                                    <td>{application?.external_customer_id}</td>
                                                    <td>{application?.existing_customer ? "Yes" : "No"}</td>
                                                    <td>{[application?.first_name, application?.middle_name, application?.last_name].filter(Boolean).join(" ")}</td>
                                                    <td>{application?.email || "N/A"}</td>
                                                    <td>{application?.phone_number}</td>
                                                    <td>{application?.aadhaar_number}</td>
                                                    <td>{application?.customer_pan}</td>
                                                    <td>
                                                        <span className="applicationStatus">
                                                            {application.parent_stage_name}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="applicationStatus">
                                                            {application.application_stage_name}
                                                        </span>
                                                    </td>
                                                    <td>{application?.createdAt ? application.createdAt.split('T')[0] : "-"}</td>
                                                    <td>{application?.updatedAt ? application.updatedAt.split('T')[0] : "-"}</td>
                                                    <td>{application?.lead_id}</td>
                                                    <td>{deleteActionOfficer}</td>
                                                    <td>{application?.deletion_reason?.officer?.role_code}</td>
                                                    <td>{application?.deletion_reason?.comment}</td>
                                                    <td>{application?.deletion_reason?.created_at ? application.deletion_reason.created_at.split('T')[0] : "-"}</td>
                                                    <td>{application?.is_rejected ? "Yes" : "No"}</td>
                                                    <td>{application?.rejected_by || "-"}</td>
                                                    <td>{application?.rejected_reason || "-"}</td>
                                                    <td>{application?.rejected_at ? application.rejected_at.split('T')[0] : "-"}</td>
                                                </tr>
                                            );
                                        })
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                            {applications?.length > 0 && (
                                <div className="paginationControls">
                                    <CustomPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </div>


                        {
                            showFilterModal && (
                                <FilterModal
                                    filters={deletedApplicationFilters}
                                    setFilters={setDeletedApplicationFilters}
                                    resetFilters={() => {
                                        let resettedFilters = resetFilters(deletedApplicationFilters);

                                        setDeletedApplicationFilters(resettedFilters);
                                        dispatch(getDeletedApplications(1, limit, resettedFilters));
                                        setShowFilterModal(false);
                                        sessionStorage.setItem("deletedApplicationFilters", JSON.stringify(resettedFilters));
                                        return resettedFilters;
                                    }}
                                    handleApplyFilters={(finalFilters) => {
                                        handlePageChange(1);
                                        dispatch(getDeletedApplications(1, limit, finalFilters));
                                        setShowFilterModal(false);
                                    }}
                                    onClose={() => {
                                        setShowFilterModal(false)
                                    }}
                                    moduleName={"Deleted Applications"}
                                    dynamicOptions={{
                                        current_application_bucket: parentStageOptions,
                                        application_stage_name: applicationStageOptions,

                                        ro_employee_ids: officerOptions.roOptions,
                                        bm_employee_ids: officerOptions.bmOptions,
                                        bcm_employee_ids: officerOptions.bcmOptions,
                                    }}
                                />
                            )
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeletedApplicationsDashboard;